import { useState, useEffect } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Cookie, X, Settings } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Switch } from "@/components/ui/switch";
import { Link } from "react-router-dom";

interface CookiePreferences {
  necessary: boolean;
  analytics: boolean;
  marketing: boolean;
  preferences: boolean;
}

const defaultPreferences: CookiePreferences = {
  necessary: true,
  analytics: false,
  marketing: false,
  preferences: false,
};

export function CookieConsent() {
  const [isVisible, setIsVisible] = useState(false);
  const [showSettings, setShowSettings] = useState(false);
  const [preferences, setPreferences] = useState<CookiePreferences>(defaultPreferences);

  useEffect(() => {
    const consent = localStorage.getItem("cookie-consent");
    if (!consent) {
      // Delay showing the banner to avoid interfering with LCP metrics
      // Wait for page to fully load and paint before showing cookie banner
      // Using 6 seconds to ensure LCP measurement completes first
      const timer = setTimeout(() => setIsVisible(true), 6000);
      return () => clearTimeout(timer);
    }
  }, []);

  const savePreferences = (prefs: CookiePreferences) => {
    localStorage.setItem("cookie-consent", JSON.stringify({
      preferences: prefs,
      timestamp: new Date().toISOString(),
    }));
    setIsVisible(false);
  };

  const acceptAll = () => {
    const allAccepted: CookiePreferences = {
      necessary: true,
      analytics: true,
      marketing: true,
      preferences: true,
    };
    savePreferences(allAccepted);
  };

  const acceptNecessary = () => {
    savePreferences(defaultPreferences);
  };

  const saveCustomPreferences = () => {
    savePreferences(preferences);
  };

  return (
    <AnimatePresence>
      {isVisible && (
        <motion.div
          initial={{ y: 100, opacity: 0 }}
          animate={{ y: 0, opacity: 1 }}
          exit={{ y: 100, opacity: 0 }}
          transition={{ type: "spring", damping: 25, stiffness: 300 }}
          className="fixed bottom-0 left-0 right-0 z-50 p-2 sm:p-4 md:p-6"
        >
          <div className="container mx-auto max-w-4xl">
            <div className="bg-card border border-border/50 rounded-xl shadow-2xl overflow-hidden">
              {/* Main Banner */}
              <AnimatePresence mode="wait">
                {!showSettings ? (
                  <motion.div
                    key="banner"
                    initial={{ opacity: 0 }}
                    animate={{ opacity: 1 }}
                    exit={{ opacity: 0 }}
                    className="p-4 sm:p-6"
                  >
                    <div className="flex flex-col sm:flex-row sm:items-start gap-3 sm:gap-4">
                      {/* Header row for mobile */}
                      <div className="flex items-center justify-between sm:contents">
                        <div className="flex items-center gap-2 sm:block">
                          <div className="w-8 h-8 sm:w-12 sm:h-12 bg-primary/10 rounded-full flex items-center justify-center flex-shrink-0">
                            <Cookie className="w-4 h-4 sm:w-6 sm:h-6 text-primary" />
                          </div>
                          <h3 className="font-semibold text-foreground text-sm sm:text-base sm:hidden">We value your privacy</h3>
                        </div>
                        <button
                          onClick={acceptNecessary}
                          className="text-muted-foreground hover:text-foreground transition-colors sm:hidden"
                          aria-label="Close cookie banner"
                        >
                          <X className="w-5 h-5" />
                        </button>
                      </div>
                      <div className="flex-1">
                        <h3 className="font-semibold text-foreground mb-2 hidden sm:block">We value your privacy</h3>
                        <p className="text-xs sm:text-sm text-muted-foreground mb-3 sm:mb-4">
                          We use cookies to enhance your experience and analyze traffic.{" "}
                          <Link to="/cookie-policy" className="text-primary underline hover:text-primary/80">
                            Learn more
                          </Link>
                        </p>
                        <div className="flex flex-wrap gap-2 sm:gap-3">
                          <Button onClick={acceptAll} size="sm" className="flex-1 text-xs sm:text-sm sm:flex-none">
                            Accept All
                          </Button>
                          <Button onClick={acceptNecessary} variant="outline" size="sm" className="flex-1 text-xs sm:text-sm sm:flex-none">
                            Necessary Only
                          </Button>
                          <Button
                            onClick={() => setShowSettings(true)}
                            variant="ghost"
                            size="sm"
                            className="w-full sm:w-auto sm:flex-none gap-1 sm:gap-2 text-xs sm:text-sm"
                          >
                            <Settings className="w-3 h-3 sm:w-4 sm:h-4" />
                            Customize
                          </Button>
                        </div>
                      </div>
                      <button
                        onClick={acceptNecessary}
                        className="hidden sm:block text-muted-foreground hover:text-foreground transition-colors"
                        aria-label="Close cookie banner"
                      >
                        <X className="w-5 h-5" />
                      </button>
                    </div>
                  </motion.div>
                ) : (
                  <motion.div
                    key="settings"
                    initial={{ opacity: 0 }}
                    animate={{ opacity: 1 }}
                    exit={{ opacity: 0 }}
                    className="p-4 sm:p-6 max-h-[70vh] overflow-y-auto"
                  >
                    <div className="flex items-center justify-between mb-4 sm:mb-6">
                      <h3 className="font-semibold text-foreground text-sm sm:text-base">Cookie Settings</h3>
                      <button
                        onClick={() => setShowSettings(false)}
                        className="text-muted-foreground hover:text-foreground transition-colors"
                        aria-label="Back to main banner"
                      >
                        <X className="w-5 h-5" />
                      </button>
                    </div>
                    
                    <div className="space-y-3 sm:space-y-4 mb-4 sm:mb-6">
                      {/* Necessary Cookies */}
                      <div className="flex items-center justify-between p-3 sm:p-4 bg-muted/30 rounded-lg gap-3">
                        <div className="flex-1 min-w-0">
                          <div className="flex items-center gap-2 flex-wrap">
                            <h4 className="font-medium text-foreground text-sm">Necessary</h4>
                            <span className="text-[10px] sm:text-xs bg-primary/10 text-primary px-1.5 sm:px-2 py-0.5 rounded">Required</span>
                          </div>
                          <p className="text-xs text-muted-foreground mt-1 hidden sm:block">
                            Essential for the website to function properly.
                          </p>
                        </div>
                        <Switch checked={true} disabled className="flex-shrink-0" />
                      </div>

                      {/* Analytics Cookies */}
                      <div className="flex items-center justify-between p-3 sm:p-4 bg-muted/30 rounded-lg gap-3">
                        <div className="flex-1 min-w-0">
                          <h4 className="font-medium text-foreground text-sm">Analytics</h4>
                          <p className="text-xs text-muted-foreground mt-1 hidden sm:block">
                            Help us understand how visitors interact.
                          </p>
                        </div>
                        <Switch
                          checked={preferences.analytics}
                          onCheckedChange={(checked) =>
                            setPreferences((prev) => ({ ...prev, analytics: checked }))
                          }
                          className="flex-shrink-0"
                        />
                      </div>

                      {/* Marketing Cookies */}
                      <div className="flex items-center justify-between p-3 sm:p-4 bg-muted/30 rounded-lg gap-3">
                        <div className="flex-1 min-w-0">
                          <h4 className="font-medium text-foreground text-sm">Marketing</h4>
                          <p className="text-xs text-muted-foreground mt-1 hidden sm:block">
                            Used for personalized advertisements.
                          </p>
                        </div>
                        <Switch
                          checked={preferences.marketing}
                          onCheckedChange={(checked) =>
                            setPreferences((prev) => ({ ...prev, marketing: checked }))
                          }
                          className="flex-shrink-0"
                        />
                      </div>

                      {/* Preference Cookies */}
                      <div className="flex items-center justify-between p-3 sm:p-4 bg-muted/30 rounded-lg gap-3">
                        <div className="flex-1 min-w-0">
                          <h4 className="font-medium text-foreground text-sm">Preferences</h4>
                          <p className="text-xs text-muted-foreground mt-1 hidden sm:block">
                            Remember your settings for a better experience.
                          </p>
                        </div>
                        <Switch
                          checked={preferences.preferences}
                          onCheckedChange={(checked) =>
                            setPreferences((prev) => ({ ...prev, preferences: checked }))
                          }
                          className="flex-shrink-0"
                        />
                      </div>
                    </div>

                    <div className="flex flex-wrap gap-2 sm:gap-3">
                      <Button onClick={saveCustomPreferences} size="sm" className="flex-1 text-xs sm:text-sm sm:flex-none">
                        Save Preferences
                      </Button>
                      <Button onClick={acceptAll} variant="outline" size="sm" className="flex-1 text-xs sm:text-sm sm:flex-none">
                        Accept All
                      </Button>
                    </div>
                  </motion.div>
                )}
              </AnimatePresence>
            </div>
          </div>
        </motion.div>
      )}
    </AnimatePresence>
  );
}
